// sheet_history.js — make bottom sheets history-aware so the Android / browser
// Back button (and the iOS standalone edge swipe) closes the top sheet instead
// of leaving the screen underneath it.
//
// Every open sheet pushes one history entry. Back pops it and closes the sheet.
// Closing a sheet from the UI removes its entry again (history.back(), with the
// resulting popstate ignored). The store's popstate handler asks
// handleSheetPop() first and only pops a route when no sheet consumed the event.

import React from 'react';

const stack = [];      // [{ id, close }] — open sheets, top last
let ignorePops = 0;    // popstates we caused ourselves
let nextId = 1;

function opened(id, close) {
  stack.push({ id, close });
  try { window.history.pushState({ llSheet: id }, ''); } catch (_) {}
}

function closed(id) {
  const i = stack.findIndex(e => e.id === id);
  if (i < 0) return;
  stack.splice(i, 1);
  // Only rewind when our entry is still the current one. If a route was pushed
  // on top (the sheet's action navigated), the stale entry is skipped later.
  try {
    if (window.history.state && window.history.state.llSheet === id) {
      ignorePops++;
      window.history.back();
    }
  } catch (_) {}
}

// Returns true when the popstate was consumed by a sheet (do not pop a route).
export function handleSheetPop(e) {
  if (ignorePops > 0) { ignorePops--; return true; }
  if (stack.length) {
    const top = stack.pop();
    try { top.close(); } catch (_) {}
    return true;
  }
  // Landed on a stale sheet entry (its sheet closed while a route sat above it):
  // let the route pop, then step over the dead entry.
  if (e && e.state && e.state.llSheet) {
    ignorePops++;
    try { window.history.back(); } catch (_) { ignorePops--; }
  }
  return false;
}

export function openSheetCount() { return stack.length; }

// Subscribe-able "is any sheet open" signal (used by the toast layer so toasts
// never cover a sheet's controls).
const listeners = new Set();
let openCount = 0;
function notify() { listeners.forEach(l => { try { l(openCount); } catch (_) {} }); }
export function useAnySheetOpen() {
  const [n, setN] = React.useState(openCount);
  React.useEffect(() => { listeners.add(setN); setN(openCount); return () => listeners.delete(setN); }, []);
  return n > 0;
}

// Hook used by every sheet primitive.
export function useSheetHistory(open, onClose) {
  const closeRef = React.useRef(onClose);
  closeRef.current = onClose;
  React.useLayoutEffect(() => {
    if (!open) return;
    const id = nextId++;
    let pushed = false;
    let poppedByBack = false;
    openCount++; notify();
    // Deferred so StrictMode's mount → unmount → mount probe doesn't push twice.
    const t = setTimeout(() => {
      pushed = true;
      opened(id, () => { poppedByBack = true; closeRef.current && closeRef.current(); });
    }, 0);
    return () => {
      clearTimeout(t);
      openCount = Math.max(0, openCount - 1); notify();
      if (pushed && !poppedByBack) closed(id);
    };
  }, [open]);
}
